/**
 * Die Rechtstexte – AGB, Datenschutzerklärung, Auftragsbearbeitung, Impressum.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Warum sie hier stehen und nicht in vier HTML-Seiten
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Weil dieselben Angaben in allen vier vorkommen: Firma, Sitz, UID,
 * Kontaktadresse, Aufbewahrungsfristen, der Ort der Daten. Vier Dateien
 * hiessen vier Orte, an denen der Sitz steht – und beim nächsten Umzug stünde
 * er an drei Orten richtig und an einem falsch. Genau dieser Fehler ist auf
 * dieser Baustelle schon zweimal passiert (die GB-Angaben, die Paketnamen),
 * und bei einem Rechtstext ist er teurer als bei einer Marketingzeile.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Keine Platzhalter mehr
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Früher standen hier gelb hinterlegte Lücken («noch offen: Firma des
 * Anbieters»). Der Gedanke war richtig – eine Erklärung mit erfundener Adresse
 * ist schlimmer als keine –, aber das Ergebnis war eine Rechtsseite, die wie
 * eine Baustelle aussah.
 *
 * Der Ausweg ist nicht, die Lücken zu füllen, sondern die Sätze so zu
 * schreiben, dass sie ohne die fehlenden Angaben wahr und vollständig sind.
 * Es gibt heute keine eingetragene Firmierung und keine UID – also steht
 * keine da, und es steht auch kein Hinweis da, dass eine fehlt. Was da steht,
 * stimmt: ein Angebot einer Firma im Aufbau mit Sitz in Zürich, erreichbar
 * unter einer echten Adresse.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Was diese Texte sind – und was nicht
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Sie sind auf das zugeschnitten, was Raporto TATSÄCHLICH tut: ein Sitzungs-
 * cookie und sonst keines, keine Zählpixel, kein Werbenetz, die
 * Handelsregisterabfrage beim Bund als einziger ausgehender Dienst auf
 * Eingabe hin, Trennung der Mandanten über Zeilensicherheit in der Datenbank,
 * zweiter Faktor. Nichts davon ist aus einer Vorlage abgeschrieben.
 *
 * Seit Oktober 2026 gehört die Besuchszählung des Hosting-Anbieters dazu
 * (Vercel Web Analytics, eingehängt in `main.tsx`). Hier stand vorher «kein
 * fremder Auswerter» und darunter im Text «keine externen Analysedienste» –
 * zwei Sätze, die mit dem Einschalten falsch geworden wären. Sie sind
 * ersetzt, nicht ergänzt. Was die Zählung festhält, steht im Abschnitt
 * «Cookies und Besuchszählung», und es steht dort so, wie der Code es tut:
 * ohne Cookie, und ohne den Teil der Adresse hinter dem Fragezeichen, weil
 * dort Zugangsmerkmale stehen können.
 *
 * In dieser Aufzählung stand einmal «tägliche Sicherung». Sie gab es nie –
 * der Satz war aus einer Vorlage übernommen und hat es von hier bis in die
 * AGB, in den AVV und in die Vergleichstabelle geschafft. Wer hier etwas
 * hinzufügt, prüft es vorher im Code.
 *
 * Sie ersetzen keine anwaltliche Prüfung. Besonders der AVV und die
 * Datenschutzerklärung gehören vor dem ersten zahlenden Kunden einmal
 * angesehen – nicht weil hier geraten wurde, sondern weil die Folgen eines
 * Fehlers den Aufwand einer Stunde Beratung um Grössenordnungen übersteigen.
 */

/**
 * Die Angaben zum Anbieter – an genau einer Stelle.
 *
 * Nur Felder, die einen belegbaren Wert haben. Eine Firmierung, eine
 * Rechtsform und eine UID gibt es heute nicht; sie fehlen deshalb nicht nur
 * im Text, sondern auch in diesem Objekt. Ein Feld mit `null` wäre eine
 * Einladung, es später mit irgendetwas zu füllen.
 */
export interface Anbieter {
  /** Wie sich der Betreiber heute wahrheitsgemäss beschreiben lässt. */
  betreiber: string
  sitz: string
  land: string
  email: string
}

export const ANBIETER: Anbieter = {
  betreiber:
    'Raporto wird von einer Firma in Gründung mit Sitz in Zürich betrieben.',
  sitz: 'Zürich',
  land: 'Schweiz',
  email: 'info@raporto.ch'
}

/** Welche Angabe hinter welcher Marke steht. */
export const MARKEN: Record<string, () => string> = {
  betreiber: () => ANBIETER.betreiber,
  sitz: () => ANBIETER.sitz,
  land: () => ANBIETER.land,
  email: () => ANBIETER.email
}

/*
  Ein Absatz ist Text oder eine Liste. Sonst nichts.

  Es gab hier ein drittes Feld, `betont`, für Sätze, die etwas zusichern. Sie
  standen dann abgesetzt, mit Kante und eigener Fläche. Die Absicht war, dass
  man sie nicht überliest; das Ergebnis war eine Rechtsseite voller Kästen –
  und ein Kasten um «Raporto wird von einer Firma in Gründung mit Sitz in
  Zürich betrieben» liest sich wie eine Entschuldigung für den Satz, den er
  umgibt.

  Eine ordentliche AGB hebt nichts hervor. Sie ist durchgehend gesetzt, und
  wer sie liest, liest sie ganz. Deshalb kann hier kein Absatz mehr eine
  Sonderbehandlung verlangen – es gibt kein Feld dafür.
*/
export interface Absatz {
  text?: string
  liste?: string[]
  /** Halbfett und in voller Tinte – für die Zeile, die den Namen trägt. */
  stark?: boolean
}

export interface Abschnitt {
  titel: string
  absaetze: Absatz[]
}

export interface Rechtstext {
  id: 'agb' | 'datenschutz' | 'avv' | 'impressum'
  titel: string
  unter: string
  /*
    Der Titel im Seitenkopf ist ein anderer als der im Menü.

    Im Kopf steht er in 76 Pixeln und darf zweiteilig sein – der zweite Teil
    bekommt den Farbverlauf. «Allgemeine Geschäftsbedingungen» ist dafür ein
    Wort zu lang; dort steht «Allgemeine» und farbig «Geschäftsbedingungen».
    Fehlen die Felder, nimmt der Kopf den Titel unverändert.
  */
  kopf?: string
  kopfHell?: string
  /** Zwei bis vier Zeilen unter dem Titel – worum es in diesem Dokument geht. */
  vorspann?: string
  /** Fassung, damit ein Betrieb weiss, welche er gelesen hat. */
  stand: string
  abschnitte: Abschnitt[]
}

/*
  Die Fassung. Sie ändert sich mit dem Text und nicht mit dem Ausrollen.

  Geändert am 11. Oktober 2026: Der Abschnitt «Zahlung, Nachfrist und
  Lesemodus» ist neu – er beschreibt, was nach der Testphase geschieht, wenn
  eine Abbuchung nicht durchgeht. Die Dauer der Testphase ist unverändert
  dreissig Tage; eine Zwischenfassung mit sieben Tagen war ein Irrtum des
  Entwicklers und ist nie in Kraft getreten.
*/
/*
  Geändert am 21. Oktober 2026: Der Abschnitt «Cookies» heisst jetzt «Cookies
  und Besuchszählung» und beschreibt die Besuchszählung des Hosting-Anbieters.
  Zwei Sätze waren mit dem Einschalten falsch geworden – «keine externen
  Analysedienste» im Text und «kein fremder Auswerter» im Kommentar oben – und
  sind ersetzt. Bei Vercel steht die Zählung jetzt in der Liste der
  Dienstleister, in der Datenschutzerklärung wie im Auftragsbearbeitungs-
  vertrag.

  Die Fassung gilt für alle vier Texte gemeinsam, wie am 11. Oktober. Geändert
  hat diesmal nur die Datenschutzerklärung; AGB, AVV und Impressum tragen das
  neue Datum, ohne dass sich ihr Inhalt geändert hat.
*/
const STAND = 'Zuletzt aktualisiert · 21. Oktober 2026'

/* ===========================================================================
   Impressum
   =========================================================================== */

const IMPRESSUM: Rechtstext = {
  id: 'impressum',
  titel: 'Impressum',
  unter: 'Wer hinter Raporto steht.',
  kopf: 'Impressum',
  vorspann:
    'Identitäts- und Kontaktangaben zu Raporto gemäss den anwendbaren Schweizer ' +
    'Transparenzpflichten.',
  stand: STAND,
  abschnitte: [
    {
      titel: 'Unternehmen',
      absaetze: [
        { text: '{{betreiber}}', stark: true },
        {
          text:
            'Raporto ist eine Schweizer Software für Handwerks- und ' +
            'Dienstleistungsbetriebe: Rapporte, Offerten, Rechnungen und Zahlungen. ' +
            'Sie wird als Abonnement bereitgestellt.'
        },
        { text: 'Sitz: {{sitz}}, {{land}}' }
      ]
    },
    {
      titel: 'Verantwortliche Stelle',
      absaetze: [
        { text: '{{betreiber}}', stark: true },
        { text: 'Sitz: {{sitz}}, {{land}}' },
        { text: 'E-Mail: {{email}}' }
      ]
    },
    {
      titel: 'Kontakt',
      absaetze: [
        { text: 'E-Mail: {{email}}' },
        /*
          Keine Telefonnummer.

          Nicht aus Prinzip, sondern weil es keine gibt. Eine Nummer, unter der
          niemand abnimmt, ist schlechter als keine – und eine Nummer, die
          jemand erfindet, damit die Seite vollständig aussieht, ist der
          Anfang genau der Unordnung, gegen die diese Seite geschrieben ist.
        */
        {
          text:
            'Anfragen zur Bedienung, zum Datenschutz und zu rechtlichen Fragen laufen ' +
            'über dieselbe Adresse. Wir antworten schriftlich.'
        }
      ]
    },
    {
      titel: 'Zweck dieser Seite',
      absaetze: [
        {
          text:
            'Raporto ist eine Software für Schweizer Handwerksbetriebe: Offerten, ' +
            'Rapporte, Rechnungen und Zahlungen. Sie wird als Abonnement bereitgestellt.'
        },
        {
          text:
            'Raporto erbringt keine Rechts-, Steuer- oder Treuhandberatung. Die ' +
            'steuerliche und buchhalterische Beurteilung der erstellten Belege ' +
            'obliegt dir und deiner Treuhandstelle.'
        }
      ]
    },
    {
      titel: 'Urheberrecht',
      absaetze: [
        {
          text:
            'Die Inhalte dieser Seite und die Software stehen im Eigentum des Betreibers. ' +
            'Die Rechte an den von dir erfassten Daten verbleiben bei dir; Einzelheiten ' +
            'regeln die AGB und der Vertrag zur Auftragsbearbeitung.'
        }
      ]
    },
    {
      /*
        Der Abschnitt heisst «Eintragung» und sagt, dass es keine gibt.

        Das ist etwas anderes als eine Lücke: eine Lücke lässt offen, ob die
        Angabe existiert und nur fehlt. Hier steht, dass sie nicht existiert,
        und warum. Wer prüft, ob er es mit einer eingetragenen Gesellschaft zu
        tun hat, bekommt hier eine Antwort statt eines leeren Feldes.
      */
      titel: 'Eintragung',
      absaetze: [
        {
          text:
            'Die Gründung ist im Gang. Solange sie nicht abgeschlossen ist, bestehen ' +
            'weder eine Eintragung noch eine Mehrwertsteuernummer, die sich hier ' +
            'ausweisen liessen. Sobald beides vorliegt, steht es an dieser Stelle.'
        }
      ]
    }
  ]
}

/* ===========================================================================
   AGB
   =========================================================================== */

const AGB: Rechtstext = {
  id: 'agb',
  titel: 'Allgemeine Geschäftsbedingungen',
  unter: 'Was gilt zwischen deinem Betrieb und uns.',
  kopf: 'Allgemeine',
  kopfHell: 'Geschäftsbedingungen',
  vorspann:
    'Diese Bedingungen regeln die Nutzung der Website und der Software Raporto. ' +
    'Raporto wird von einer Firma in Gründung mit Sitz in Zürich betrieben und ' +
    'richtet sich an Unternehmen. Der Vertrag untersteht Schweizer Recht. Lies sie ' +
    'bitte sorgfältig – mit der Bestellung bestätigst du ihre Annahme.',
  stand: STAND,
  abschnitte: [
    {
      /*
        Die Zusammenfassung steht vorn und ist keine Rechtsgrundlage.

        Kein Betrieb liest zwölf Abschnitte, bevor er ein Abonnement bestellt –
        er überfliegt und sucht die drei Punkte, die weh tun könnten: Bindung,
        Kosten, Daten. Stehen sie vorn und stimmen sie mit dem Rest überein,
        liest er weiter. Stehen sie nicht da, liest er gar nicht und ärgert
        sich später über etwas, das schon immer dastand.

        Der letzte Satz ist wichtig: diese Kurzfassung geht keiner Klausel vor.
        Sonst hätte man zwei Fassungen desselben Vertrags.
      */
      titel: 'Auf einen Blick',
      absaetze: [
        {
          /*
            Jeder Punkt beginnt mit einem Stichwort und einem Punkt.

            Die Darstellung trennt daran und setzt das Stichwort halbfett. So
            findet jemand, der nur wissen will, wie lange er gebunden ist, die
            Zeile beim Überfliegen – ohne dass die Daten ein zweites Feld
            dafür brauchen.
          */
          liste: [
            'Wer wir sind. Raporto wird von einer Firma in Gründung mit Sitz in Zürich betrieben und richtet sich an Unternehmen, nicht an Konsumenten.',
            'Was du bekommst. Ein Abonnement für Rapporte, Offerten, Rechnungen und Zahlungen, bereitgestellt über das Internet, im Umfang des gewählten Pakets.',
            'Testphase. Dreissig Tage kostenlos und im vollen Funktionsumfang. Die Abrechnung beginnt erst danach.',
            'Laufzeit. Keine Mindestlaufzeit. Kündbar auf das Ende des laufenden Abrechnungsmonats, ohne Frist und ohne Begründung.',
            'Deine Daten. Sie gehören dir. Export während der ganzen Vertragsdauer, Löschung auf deine Aufforderung.',
            'Was wir nicht zusichern. Eine Verfügbarkeit in Prozent. Wir nennen keine Zahl, die wir nicht messen.',
            'Recht und Gerichtsstand. Schweizer Recht, Gerichtsstand Zürich.'
          ]
        },
        {
          text:
            'Diese Übersicht fasst zusammen und ersetzt nichts. Massgebend sind die ' +
            'Abschnitte darunter.'
        }
      ]
    },
    {
      titel: 'Vertragsparteien',
      absaetze: [
        /*
          Wer «wir» ist, steht hier im Klartext und nicht nur im Impressum.

          Ein Betrieb, der diese Bedingungen seinem Treuhänder vorlegt, soll
          die Gegenpartei im Dokument selbst finden – nicht auf einer anderen
          Seite nachschlagen müssen. Drei Zeilen, normaler Fliesstext.
        */
        { text: '{{betreiber}}', stark: true },
        { text: 'Sitz: {{sitz}}, {{land}}' },
        { text: 'E-Mail: {{email}}' },
        {
          text:
            'Diese Bedingungen gelten zwischen dem Betreiber von Raporto («wir», ' +
            '«Raporto») und dem Betrieb, der einen Zugang bestellt («du», «Betrieb»).'
        },
        {
          text:
            'Raporto richtet sich an Unternehmen. Der Vertrag ist kein ' +
            'Konsumentenvertrag; die Bestimmungen über Haustürgeschäfte und ' +
            'Widerrufsrechte für Konsumenten gelten nicht.'
        }
      ]
    },
    {
      titel: 'Leistungsumfang',
      absaetze: [
        {
          text:
            'Wir stellen Raporto über das Internet bereit, im Umfang des gewählten ' +
            'Pakets. Was in welchem Paket enthalten ist, steht auf der Preisseite und ' +
            'ist Bestandteil dieses Vertrags.'
        },
        {
          text:
            'Wir entwickeln Raporto weiter. Funktionen können dazukommen und sich ' +
            'ändern. Eine Funktion, auf die du dich verlässt, entfernen wir nicht ohne ' +
            'Vorankündigung von mindestens drei Monaten.'
        },
        {
          text:
            'Eine Verfügbarkeit in Prozent sichern wir nicht zu.'
        },
        {
          text:
            'Wartungsarbeiten kündigen wir an, wenn sie planbar sind, und legen sie ' +
            'ausserhalb der üblichen Bürozeiten.'
        }
      ]
    },
    {
      titel: 'Zugänge',
      absaetze: [
        {
          text:
            'Ein Zugang gehört genau einem Betrieb. Wer zwei Firmen führt, benötigt zwei ' +
            'Zugänge. Die Daten verschiedener Betriebe sind dadurch technisch getrennt.'
        },
        {
          text:
            'Die Anzahl zugelassener Personen ergibt sich aus dem gewählten Paket. Jede ' +
            'Person, die mit Raporto arbeitet, benötigt einen eigenen Zugang; Zugänge ' +
            'dürfen nicht geteilt werden.'
        },
        {
          text:
            'Du sorgst dafür, dass die Zugangsdaten deiner Leute geheim bleiben, und ' +
            'meldest uns, wenn ein Zugang abhandengekommen ist. Wir sperren ihn dann ' +
            'unverzüglich.'
        },
        {
          text:
            'Die Zwei-Faktor-Anmeldung steht jeder Person zur Verfügung und lässt sich in ' +
            'den Einstellungen einrichten.'
        }
      ]
    },
    {
      titel: 'Zulässige Nutzung',
      absaetze: [
        {
          liste: [
            'Raporto als allgemeine Dateiablage nutzen. Der Speicher ist für Belege und Baustellenfotos bestimmt, nicht für Videos, Sicherungskopien oder private Bildersammlungen.',
            'Zugänge an Dritte weitergeben oder Raporto an Dritte weitervermieten.',
            'Rechtswidrige Inhalte ablegen oder Raporto für rechtswidrige Zwecke nutzen.',
            'Die Software nachbauen, entschlüsseln oder Sicherheitsvorkehrungen umgehen.',
            'Die Plattform automatisiert in einem Mass belasten, das den Betrieb für andere Kunden beeinträchtigt.'
          ]
        },
        {
          text:
            'Bei einem schweren oder wiederholten Verstoss können wir den Zugang sperren. ' +
            'Wir teilen dir die Beanstandung vorgängig mit und setzen eine angemessene ' +
            'Frist zur Behebung, sofern die Sperrung keinen Aufschub duldet.'
        }
      ]
    },
    {
      titel: 'Speicher',
      absaetze: [
        {
          text:
            'Jedes Paket enthält eine Speichermenge für Belege und Fotos. Ist sie ' +
            'erreicht, nehmen wir keine neuen Fotos mehr an.'
        },
        {
          text:
            'Rechnungen und Offerten lassen sich auch bei erreichter Speichergrenze ' +
            'unverändert erstellen und abschliessen.'
        }
      ]
    },
    {
      titel: 'Preise, Rechnungsstellung und Änderungen',
      absaetze: [
        {
          text:
            'Es gelten die Preise des gewählten Pakets zum Zeitpunkt der Bestellung. ' +
            'Alle Beträge verstehen sich ohne Mehrwertsteuer; der Normalsatz beträgt in ' +
            'der Schweiz zurzeit 8.1 %.'
        },
        {
          text:
            'Die Rechnungsstellung erfolgt monatlich oder jährlich im Voraus, je nach ' +
            'deiner Wahl. Beim Jahresmodell bezahlst du zehn Monate für zwölf.'
        },
        {
          text:
            'Preisänderungen kündigen wir mindestens zwei Monate im Voraus in ' +
            'Textform an. Bist du nicht einverstanden, kannst du bis zum Wirksamwerden ' +
            'auf dieses Datum kündigen. Der bereits bezahlte Zeitraum bleibt zum alten ' +
            'Preis.'
        },
        {
          text:
            'Bei Zahlungsverzug mahnen wir zweimal. Bleibt die Zahlung danach aus, ' +
            'können wir den Zugang sperren. Deine Daten löschen wir deswegen nicht – ' +
            'dafür gilt Ziffer 9.'
        }
      ]
    },
    {
      titel: 'Laufzeit und Kündigung',
      absaetze: [
        {
          text:
            'Der Vertrag läuft unbefristet. Es gibt keine Mindestlaufzeit und keine ' +
            'Einrichtungsgebühr.'
        },
        {
          text:
            'Du kannst jederzeit auf das Ende des laufenden Abrechnungsmonats kündigen. ' +
            'Beim Jahresmodell endet der Vertrag auf das Ende der bezahlten Periode.'
        },
        {
          text:
            'Wir können mit einer Frist von drei Monaten auf ein Monatsende kündigen. Die ' +
            'längere Frist trägt dem Umstand Rechnung, dass ein Wechsel der Software ' +
            'Vorbereitung erfordert.'
        }
      ]
    },
    {
      titel: 'Testphase',
      absaetze: [
        {
          /*
            Die Dauer steht hier ausgeschrieben – und zwar mit Absicht.

            Sie aus `TESTPHASE_TAGE` einzusetzen wäre technisch sauberer und
            rechtlich falsch: Dann änderte eine Codezeile den Vertragstext,
            ohne dass jemand eine neue Fassung beschliesst, und der Betrieb
            läse morgen etwas anderes als das, was er gelesen hat. Ein
            Vertragstext hat eine Fassung und ein Datum.

            Dass Zahl und Programm trotzdem nicht auseinanderlaufen, sichert
            `tests/rechtstexte.test.ts`: Steht in `TESTPHASE_TAGE` eine andere
            Zahl als hier, wird die Prüfung rot – und dann ist eine neue
            Fassung fällig, keine stille Anpassung. Genau das hat den kurzen
            Irrtum mit sieben Tagen auch wieder eingefangen.

            Die Fristen NACH der Testphase stehen bewusst nicht hier, sondern
            im Abschnitt «Zahlung, Nachfrist und Lesemodus»: Sie betreffen
            einen Vertrag, der schon läuft.
          */
          text:
            'Die Testphase dauert 30 Tage und ist kostenlos. Sie endet von selbst; es ' +
            'entsteht daraus keine Zahlungspflicht, wenn du nichts weiter tust.'
        }
      ]
    },
    {
      /*
        NEU in dieser Fassung – und der Grund, warum sie eine neue ist.

        Was nach einer fehlgeschlagenen Abbuchung geschieht, stand bisher
        nirgends: nicht im Vertrag, nicht auf dem Bildschirm. Die Fristen
        existierten im Programm (`config.ts`: TEST_ARCHIV_TAGE,
        AUSSTIEG_LOESCHUNG_TAGE) und wurden auch berechnet – ein Kunde konnte
        sie nur nicht lesen.

        Die Zahlen stehen auch hier ausgeschrieben, aus demselben Grund wie
        bei der Testphase, und `tests/rechtstexte.test.ts` hält sie mit den
        Werten im Programm zusammen.

        ACHTUNG, und das ist für den Betreiber und nicht für den Kunden: Der
        letzte Absatz kündigt eine Löschung an. Ausgeführt wird sie erst, wenn
        `AUSSTIEG_PURGE_AKTIV` eingeschaltet ist – bis dahin bleiben die Daten
        liegen. Ein Vertragstext, der mehr verspricht, als das Programm tut,
        ist hier die harmlosere Richtung (es wird nichts gelöscht, was der
        Text schützt); umgekehrt wäre es ein Schaden.
      */
      titel: 'Zahlung, Nachfrist und Lesemodus',
      absaetze: [
        {
          text:
            'Vor der ersten Abbuchung melden wir uns rechtzeitig und nennen dir den Tag, ' +
            'an dem abgebucht wird. Geht die Zahlung durch, läuft dein Abo normal weiter.'
        },
        {
          text:
            'Geht eine Zahlung nicht durch, hast du 7 Tage Nachfrist. In dieser Zeit ' +
            'arbeitest du unverändert weiter, und wir sagen dir, woran es liegt.'
        },
        {
          text:
            'Bleibt die Zahlung auch danach aus, wechselt dein Zugang für weitere 7 Tage ' +
            'in den Lesemodus: Du siehst alle deine Daten, kannst sie exportieren und die ' +
            'Zahlung nachholen – neue Rechnungen, Offerten und Rapporte entstehen in ' +
            'dieser Zeit keine.'
        },
        {
          text:
            'Holst du die Zahlung nach, steht dein Zugang sofort wieder vollständig offen, ' +
            'und eine vorgemerkte Löschung entfällt.'
        },
        {
          text:
            'Bleibt die Zahlung aus, ist deine Betriebsumgebung frühestens 14 Tage nach ' +
            'dem Ende des bezahlten Zeitraums zur Löschung vorgesehen. Wir löschen nicht ' +
            'ohne vorherige Ankündigung mit konkretem Datum im Tool und per E-Mail; ' +
            'solange wir dir keinen Tag genannt haben, bleiben deine Daten. Für deine ' +
            'gesetzliche Aufbewahrungspflicht (Artikel 958f des Obligationenrechts, zehn ' +
            'Jahre) bist du verantwortlich – sichere deinen Export, solange der Lesemodus ' +
            'läuft.'
        }
      ]
    },
    {
      titel: 'Deine Daten und ihre Rückgabe',
      absaetze: [
        {
          text:
            'Die Rechte an den von dir erfassten Daten verbleiben bei dir. Wir nutzen ' +
            'sie ausschliesslich zur Erbringung der vertraglichen Leistung, geben sie ' +
            'nicht an Dritte weiter und werten sie nicht zu Werbezwecken aus.'
        },
        {
          text:
            'Beim Aufhören bekommst du einen Export deiner Kunden, Rechnungen und ' +
            'Zahlungen als Datei sowie die abgeschlossenen Belege als PDF. Weitere ' +
            'Bestände geben wir auf Anfrage heraus.'
        },
        {
          text:
            'Nach Vertragsende halten wir deine Daten bereit, damit du den Export in Ruhe ' +
            'holen kannst. Auf deine Aufforderung hin löschen wir sie und bestätigen ' +
            'die Löschung. Aus technischen Sicherungen verschwinden sie, sobald deren ' +
            'Aufbewahrungszeitraum beim jeweiligen Dienstleister abgelaufen ist.'
        },
        {
          text:
            'Geschäftsbücher und Buchungsbelege sind nach Artikel 958f des ' +
            'Obligationenrechts zehn Jahre aufzubewahren. Diese Pflicht trifft dich als ' +
            'Betrieb. Sichere den Export deshalb, bevor du die Löschung verlangst.'
        }
      ]
    },
    {
      titel: 'Haftung',
      absaetze: [
        {
          text:
            'Wir haften für Schaden, den wir absichtlich oder grobfahrlässig verursachen, ' +
            'sowie für Personenschäden, unbeschränkt.'
        },
        {
          text:
            'Im Übrigen ist unsere Haftung auf den Betrag beschränkt, den du in den ' +
            'zwölf Monaten vor dem schädigenden Ereignis bezahlt hast. Nicht gehaftet ' +
            'wird für entgangenen Gewinn und für Folgeschäden.'
        },
        {
          text:
            'Für den Inhalt der erstellten Belege bist du verantwortlich. Raporto ' +
            'berechnet, nummeriert und archiviert; die inhaltliche und steuerliche ' +
            'Richtigkeit eines Belegs liegt in deiner Verantwortung.'
        }
      ]
    },
    {
      titel: 'Änderungen dieser Bedingungen',
      absaetze: [
        {
          text:
            'Wir können diese Bedingungen ändern. Änderungen teilen wir mindestens zwei ' +
            'Monate vorher in Textform mit. Widersprichst du nicht bis zum Wirksamwerden, ' +
            'gelten sie als angenommen; darauf weisen wir in der Mitteilung ausdrücklich ' +
            'hin. Widersprichst du, kannst du auf dieses Datum kündigen.'
        }
      ]
    },
    {
      titel: 'Anwendbares Recht und Gerichtsstand',
      absaetze: [
        {
          text:
            'Es gilt schweizerisches Recht unter Ausschluss des Übereinkommens der ' +
            'Vereinten Nationen über Verträge über den internationalen Warenkauf.'
        },
        { text: 'Ausschliesslicher Gerichtsstand ist {{sitz}}.' }
      ]
    },
    {
      /*
        Jedes Dokument endet mit der Adresse, an die man schreibt.

        Nicht nur im Impressum: wer die AGB liest, hat die Frage GENAU DORT –
        und ein Sprung auf eine andere Seite ist die Stelle, an der aus einer
        Frage keine E-Mail wird.
      */
      titel: 'Kontakt',
      absaetze: [
        { text: 'Fragen zu diesem Dokument beantworten wir schriftlich.' },
        { text: 'E-Mail: {{email}}' },
        { text: '{{betreiber}}', stark: true },
        { text: 'Sitz: {{sitz}}, {{land}}' }
      ]
    }
  ]
}

/* ===========================================================================
   Datenschutzerklärung
   =========================================================================== */

const DATENSCHUTZ: Rechtstext = {
  id: 'datenschutz',
  titel: 'Datenschutzerklärung',
  kopf: 'Datenschutz',
  kopfHell: 'erklärung',
  unter: 'Welche Daten wir bearbeiten, wozu und wie lange.',
  vorspann:
    'Diese Erklärung beschreibt, welche Personendaten wir bearbeiten, zu welchem ' +
    'Zweck und wie lange. Sie richtet sich nach dem revidierten Schweizer ' +
    'Datenschutzgesetz; soweit die europäische Datenschutz-Grundverordnung ' +
    'anwendbar ist, gilt sie zusätzlich.',
  stand: STAND,
  abschnitte: [
    {
      titel: 'Verantwortlich',
      absaetze: [
        { text: '{{betreiber}}', stark: true },
        { text: 'Sitz: {{sitz}}, {{land}}' },
        { text: 'E-Mail: {{email}}' },
        {
          text:
            'Diese Erklärung richtet sich nach dem revidierten Schweizer ' +
            'Datenschutzgesetz, das seit dem 1. September 2023 in Kraft ist. Soweit die ' +
            'europäische Datenschutz-Grundverordnung anwendbar ist, gilt sie zusätzlich.'
        }
      ]
    },
    {
      titel: 'Unsere beiden Rollen',
      absaetze: [
        {
          text:
            'Raporto bearbeitet Personendaten in zwei unterschiedlichen Rollen. Diese ' +
            'Erklärung betrifft die erste davon.'
        },
        {
          liste: [
            'Für die Daten unserer Kundschaft, also der Betriebe mit einem Abonnement, sind wir verantwortlich. Diese Erklärung beschreibt deren Bearbeitung.',
            'Für die Daten, die ein Betrieb in Raporto erfasst – seine Kunden, seine Mitarbeitenden, seine Baustellen – ist der Betrieb verantwortlich. Wir bearbeiten sie als Auftragsbearbeiter. Massgebend ist dafür der Vertrag zur Auftragsbearbeitung.'
          ]
        },
        {
          text:
            'Die Bearbeitung der Daten deiner eigenen Kunden ist im Vertrag zur ' +
            'Auftragsbearbeitung geregelt, nicht in dieser Erklärung.'
        }
      ]
    },
    {
      titel: 'Bearbeitete Daten',
      absaetze: [
        {
          liste: [
            'Bestellangaben: Firma, Adresse, UID, gewähltes Paket.',
            'Angaben zur verantwortlichen Person: Name, E-Mail, Telefon.',
            'Zugangsdaten: E-Mail-Adresse sowie das Passwort, das ausschliesslich als kryptografischer Prüfwert und nicht im Klartext gespeichert wird. Zusätzlich, sofern eingerichtet, das Geheimnis des zweiten Faktors.',
            'Nutzungsdaten: Zeitpunkt von Anmeldungen, IP-Adresse und Gerätebeschreibung der Sitzungen, sowie ein Protokoll der Aktionen, die etwas verändert haben.',
            'Zahlungsdaten: Rechnungsstellung und Zahlungseingänge.'
          ]
        },
        {
          text:
            'Das Aktionsprotokoll dient dem Nachweis, wer eine Rechnung abgeschlossen ' +
            'oder die Bankverbindung geändert hat. Es lässt sich im laufenden Betrieb ' +
            'nicht löschen und wird mit den übrigen Daten nach Vertragsende entfernt.'
        }
      ]
    },
    {
      titel: 'Zwecke der Bearbeitung',
      absaetze: [
        {
          liste: [
            'Um den Vertrag zu erfüllen: Zugang bereitstellen, Rechnung stellen, Support leisten.',
            'Um die Sicherheit zu wahren: Missbrauch erkennen, Zugänge sperren, Vorfälle nachvollziehen.',
            'Um gesetzliche Pflichten zu erfüllen, namentlich die Buchführungs- und Aufbewahrungspflichten des Obligationenrechts.'
          ]
        },
        {
          text: 'Wir bearbeiten deine Daten nicht für Werbung Dritter und verkaufen sie ' + 'nicht.'
        }
      ]
    },
    {
      titel: 'Cookies und Besuchszählung',
      absaetze: [
        {
          text:
            'Raporto setzt ein einziges Cookie, das der Anmeldesitzung. Es ist für den ' +
            'Betrieb der Anwendung erforderlich; für ein notwendiges Cookie braucht es ' +
            'keine Einwilligung.'
        },
        {
          text:
            'Für die Besuchszählung verwenden wir die Auswertung unseres Hosting-Anbieters ' +
            'Vercel (Vercel Web Analytics). Sie setzt kein Cookie und legt keine Kennung ' +
            'in deinem Browser ab. Besuche werden nach Angabe des Anbieters über einen ' +
            'Hashwert aus der Anfrage unterschieden, der nach einem Tag verfällt; eine ' +
            'Wiedererkennung über mehrere Tage oder über andere Websites ist damit nicht ' +
            'möglich.'
        },
        {
          liste: [
            'Festgehalten werden: Zeitpunkt, aufgerufene Seite, verweisende Seite, grobe Herkunft nach Land und Region, Betriebssystem, Browser und Geräteart.',
            'Nicht festgehalten wird deine IP-Adresse. Sie wird für die Bildung des Hashwerts verarbeitet, aber nicht gespeichert.',
            'Der Teil der Adresse hinter dem Fragezeichen wird entfernt, bevor etwas übermittelt wird. Links aus E-Mails und Rückkehradressen der Zahlung enthalten Zugangsmerkmale – die verlassen Raporto nicht.'
          ]
        },
        {
          text:
            'Es werden keine Zählpixel, Werbe-Cookies, Profile, Einbindungen sozialer ' +
            'Netzwerke und keine weiteren Auswertungsdienste eingesetzt. Die Zählung ' +
            'dient der Frage, welche Seiten benutzt werden – nicht der Beobachtung ' +
            'einzelner Personen.'
        }
      ]
    },
    {
      titel: 'Bekanntgabe an Dritte',
      absaetze: [
        {
          text:
            'Wir beziehen die folgenden technischen Dienstleister ein. Datenhaltung und ' +
            'Verarbeitung finden in der Schweiz und/oder der EU statt, je nach ' +
            'Dienstleister:'
        },
        {
          liste: [
            'Supabase – Betrieb der Datenbank. Die Datenbank selbst steht in der Region Zürich. Der Anbieter hat seinen Sitz ausserhalb der Schweiz; ein administrativer Zugriff aus dem Ausland lässt sich technisch nicht ausschliessen.',
            'Vercel – Betrieb der Anwendung, der vorgelagerten Firewall und der Besuchszählung (siehe oben). Dabei werden Verbindungsdaten wie IP-Adressen verarbeitet. Der Anbieter hat seinen Sitz ausserhalb der Schweiz.',
            'Infomaniak Swiss Backup – Ablage von Belegen, Baustellenfotos und Logos. Schweizer Anbieter, Infrastruktur in der Schweiz.',
            'Zefix, das Handelsregister des Bundes. Eine Abfrage erfolgt nur auf deine Eingabe hin und umfasst nur den eingegebenen Suchbegriff.'
          ]
        },
        {
          text:
            /*
              Der zweite Satz lautete: «Wir beschreiben beides getrennt, statt
              pauschal Schweiz zu schreiben.» Er sagt nichts über die Daten,
              sondern über unsere Art zu formulieren – eine Erklärung dafür,
              dass hier ordentlich gearbeitet wurde. Wer das begründen muss,
              wirkt unsicher. Der erste Satz trägt die Aussage allein.
            */
            'Der Ort der Datenbank und der Ort, an dem die Anwendung läuft, sind nicht ' +
            'dasselbe.'
        },
        {
          text:
            'Eine aktuelle Liste stellen wir auf Anfrage zur Verfügung. Das Verfahren bei ' +
            'einem Wechsel regelt Ziffer 7 des Vertrags zur Auftragsbearbeitung.'
        }
      ]
    },
    {
      titel: 'Aufbewahrungsdauer',
      absaetze: [
        {
          liste: [
            'Zugangs- und Vertragsdaten: für die Dauer des Vertrags.',
            'Sitzungsprotokolle: 12 Monate.',
            'Rechnungen und Buchungsbelege über unser eigenes Abonnement: 10 Jahre, wie es das Obligationenrecht verlangt.',
            'Alles Übrige: bis zur Löschung nach Vertragsende. Aus technischen Sicherungen verschwinden die Daten, sobald deren Aufbewahrungszeitraum beim jeweiligen Dienstleister abgelaufen ist.'
          ]
        }
      ]
    },
    {
      titel: 'Datensicherheit',
      absaetze: [
        {
          liste: [
            'Die Daten verschiedener Betriebe sind in der Datenbank technisch getrennt. Die Trennung wird auf Datenbankebene erzwungen und nicht erst durch die Anwendung geprüft.',
            'Der Zugang lässt sich mit einem zweiten Faktor absichern.',
            'Die Übertragung erfolgt verschlüsselt.',
            'Jede verändernde Aktion wird protokolliert.'
          ]
        }
      ]
    },
    {
      titel: 'Deine Rechte',
      absaetze: [
        {
          text:
            'Du kannst Auskunft über die Daten verlangen, die wir über dich bearbeiten, ' +
            'sie berichtigen oder löschen lassen und der Bearbeitung widersprechen, ' +
            'soweit sie nicht für den Vertrag oder gesetzliche Pflichten nötig ist. ' +
            'Ausserdem kannst du die Herausgabe in einem gängigen Format verlangen.'
        },
        { text: 'Wende dich dafür an {{email}}. Wir antworten innert 30 Tagen.' },
        {
          text:
            'Bist du mit unserer Antwort nicht zufrieden, kannst du dich beim ' +
            'Eidgenössischen Datenschutz- und Öffentlichkeitsbeauftragten melden.'
        }
      ]
    },
    {
      titel: 'Verletzung der Datensicherheit',
      absaetze: [
        {
          text:
            'Kommt es zu einer Verletzung der Datensicherheit, die für die betroffenen ' +
            'Personen ein hohes Risiko bedeutet, melden wir das dem Eidgenössischen ' +
            'Datenschutz- und Öffentlichkeitsbeauftragten so rasch als möglich und ' +
            'informieren die Betroffenen.'
        },
        {
          text:
            'Sind deine Daten betroffen, informieren wir dich auch dann, wenn keine ' +
            'gesetzliche Meldepflicht besteht.'
        }
      ]
    },
    {
      titel: 'Kontakt',
      absaetze: [
        { text: 'Fragen zu diesem Dokument beantworten wir schriftlich.' },
        { text: 'E-Mail: {{email}}' },
        { text: '{{betreiber}}', stark: true },
        { text: 'Sitz: {{sitz}}, {{land}}' }
      ]
    }
  ]
}

/* ===========================================================================
   Auftragsbearbeitung
   =========================================================================== */

const AVV: Rechtstext = {
  id: 'avv',
  titel: 'Vertrag zur Auftragsbearbeitung',
  kopf: 'Vertrag zur',
  kopfHell: 'Auftragsbearbeitung',
  unter: 'Was gilt, wenn wir die Daten deiner Kunden in deinem Auftrag bearbeiten.',
  vorspann:
    'Dieser Vertrag gilt zwischen deinem Betrieb als Verantwortlichem und uns als ' +
    'Auftragsbearbeiter. Er regelt, was wir mit den Daten deiner Kunden und deiner ' +
    'Mitarbeitenden tun dürfen und was nicht.',
  stand: STAND,
  abschnitte: [
    {
      titel: 'Grundlage',
      absaetze: [
        {
          text:
            'In Raporto werden Namen, Adressen und Telefonnummern deiner Kunden, die ' +
            'Namen deiner Mitarbeitenden, deren Unterschriften auf Rapporten sowie ' +
            'Fotos deiner Baustellen erfasst. Dabei handelt es sich um Personendaten, ' +
            'für die du als Betrieb verantwortlich bist.'
        },
        {
          text:
            'Wir bearbeiten diese Daten ausschliesslich in deinem Auftrag. Artikel 9 des ' +
            'Schweizer Datenschutzgesetzes verlangt hierfür eine Vereinbarung. Der ' +
            'vorliegende Vertrag gilt mit Abschluss des Abonnements und ist Bestandteil ' +
            'der AGB.'
        }
      ]
    },
    {
      titel: 'Gegenstand, Dauer, Zweck',
      absaetze: [
        {
          text:
            'Gegenstand ist die Bereitstellung von Raporto: Offerten, Rapporte, ' +
            'Rechnungen, Zahlungen und die dazugehörige Ablage von Belegen und Fotos.'
        },
        {
          text:
            'Die Bearbeitung dauert so lange wie das Abonnement und endet mit den in ' +
            'Ziffer 8 genannten Fristen.'
        },
        {
          text:
            'Die Bearbeitung erfolgt ausschliesslich zur Erbringung dieser Leistung. Eine ' +
            'Bearbeitung zu anderen Zwecken bedarf deiner ausdrücklichen Weisung.'
        }
      ]
    },
    {
      titel: 'Art der Daten und betroffene Personen',
      absaetze: [
        {
          liste: [
            'Deine Kunden: Firma oder Name, Adresse, Kontaktperson, E-Mail-Adresse, Telefonnummer sowie die von dir erfassten Notizen.',
            'Deine Mitarbeitenden: Name, E-Mail-Adresse, Rolle, Zuweisung zu Baustellen, erfasste Stunden.',
            'Unterschriften, die auf einem Gerät geleistet werden.',
            'Fotos von Baustellen, soweit sie einem Rapport beigefügt werden.',
            'Belege: Offerten, Rechnungen und deren Inhalt.'
          ]
        },
        {
          text:
            'Fotos werden ausschliesslich projektbezogen bearbeitet. Eine baustellen- ' +
            'übergreifende Galerie, eine Gesichtserkennung oder eine Auswertung der ' +
            'Bildinhalte findet nicht statt.'
        }
      ]
    },
    {
      titel: 'Weisungen',
      absaetze: [
        {
          text:
            'Wir bearbeiten die Daten ausschliesslich nach deinen Weisungen. Die ' +
            'Bedienung der Software gilt als Weisung; darüber hinausgehende Weisungen ' +
            'erfolgen in Textform.'
        },
        {
          text:
            'Halten wir eine Weisung für rechtswidrig, teilen wir dir dies vor der ' +
            'Ausführung mit.'
        },
        {
          text:
            'Müssen wir aufgrund von Schweizer Recht anders bearbeiten – etwa auf ' +
            'behördliche Anordnung –, informieren wir dich vorher, sofern uns das nicht ' +
            'gerade dieses Recht verbietet.'
        }
      ]
    },
    {
      titel: 'Vertraulichkeit',
      absaetze: [
        {
          text:
            'Alle Personen, die bei uns Zugang zu deinen Daten haben können, sind zur ' +
            'Vertraulichkeit verpflichtet; die Verpflichtung gilt über das Ende ihrer ' +
            'Tätigkeit hinaus.'
        },
        {
          text:
            'Ein Support-Zugriff auf deine Daten erfolgt nur mit Angabe eines Grundes ' +
            'und wird protokolliert. Du kannst jederzeit Einsicht in dieses Protokoll ' +
            'verlangen.'
        }
      ]
    },
    {
      titel: 'Sicherheit',
      absaetze: [
        {
          text:
            'Wir treffen die technischen und organisatorischen Massnahmen, die Artikel 8 ' +
            'des Datenschutzgesetzes und die Verordnung dazu verlangen. Konkret:'
        },
        {
          liste: [
            'Trennung der Mandanten in der Datenbank, erzwungen durch die Datenbank selbst und nicht durch die Anwendung.',
            'Verschlüsselte Übertragung.',
            'Zweiter Faktor verfügbar.',
            'Rollenbasierter Zugriff: Mitarbeitende sehen nur die ihnen zugewiesenen Baustellen und, je nach Einstellung, keine Preise.',
            'Protokoll aller verändernden Aktionen.'
          ]
        }
      ]
    },
    {
      titel: 'Unterauftragsbearbeiter',
      absaetze: [
        {
          text:
            'Wir ziehen Dritte nur mit deiner Genehmigung bei. Du genehmigst mit diesem ' +
            'Vertrag die folgenden:'
        },
        {
          liste: [
            'Supabase – Betrieb der Datenbank, Region Zürich. Sitz des Anbieters ausserhalb der Schweiz.',
            'Vercel – Betrieb der Anwendung, der Firewall und der Besuchszählung; verarbeitet Verbindungsdaten. Sitz des Anbieters ausserhalb der Schweiz.',
            'Infomaniak Swiss Backup – Ablage von Belegen und Baustellenfotos, Infrastruktur in der Schweiz.'
          ]
        },
        {
          text:
            'Datenhaltung und Verarbeitung finden in der Schweiz und/oder der EU statt, ' +
            'je nach Dienstleister. Ein administrativer Zugriff aus dem Ausland lässt ' +
            'sich bei den beiden erstgenannten technisch nicht ausschliessen.'
        },
        {
          text:
            'Einen Wechsel oder eine Ergänzung teilen wir dir mindestens 30 Tage im ' +
            'Voraus mit. Bei Widerspruch aus sachlichem Grund kannst du auf den ' +
            'Zeitpunkt der Änderung kündigen.'
        },
        {
          text:
            'Wir verpflichten jeden Unterauftragsbearbeiter auf dieselben Pflichten und ' +
            'haften für ihn wie für uns selbst.'
        }
      ]
    },
    {
      titel: 'Rückgabe und Löschung',
      absaetze: [
        {
          text:
            'Während der Vertragsdauer kannst du einen Export deiner Kunden, Rechnungen ' +
            'und Zahlungen als Datei holen; die abgeschlossenen Belege zusätzlich als ' +
            'PDF. Weitere Bestände geben wir auf Anfrage heraus.'
        },
        {
          text:
            'Nach Vertragsende halten wir die Daten für den Export bereit. Auf deine ' +
            'Aufforderung hin löschen wir sie und bestätigen die Löschung schriftlich. ' +
            'Aus technischen Sicherungen verschwinden sie, sobald deren ' +
            'Aufbewahrungszeitraum beim jeweiligen Dienstleister abgelaufen ist.'
        },
        {
          text:
            'Die zehnjährige Aufbewahrungspflicht nach Artikel 958f des ' +
            'Obligationenrechts trifft dich als Betrieb. Sichere den Export, bevor du ' +
            'die Löschung verlangst; danach können wir die Daten nicht mehr herausgeben.'
        }
      ]
    },
    {
      titel: 'Unterstützung des Verantwortlichen',
      absaetze: [
        {
          text:
            'Wendet sich eine betroffene Person an uns, etwa einer deiner Kunden mit ' +
            'einem Auskunftsbegehren, beantworten wir dieses nicht selbst, sondern ' +
            'leiten es unverzüglich an dich weiter. Die Beantwortung obliegt dir als ' +
            'Verantwortlichem.'
        },
        {
          text:
            'Wir unterstützen dich bei Auskunfts-, Berichtigungs- und Löschbegehren ' +
            'sowie bei einer allfälligen Datenschutz-Folgenabschätzung mit den Angaben, ' +
            'die nur wir haben.'
        }
      ]
    },
    {
      titel: 'Meldung von Verletzungen der Datensicherheit',
      absaetze: [
        {
          text:
            'Stellen wir eine Verletzung der Datensicherheit fest, die deine Daten ' +
            'betrifft, melden wir sie dir unverzüglich. Die Meldung umfasst den Hergang, ' +
            'die betroffenen Datenkategorien und die getroffenen Massnahmen.'
        },
        {
          text:
            'Die Meldung erfolgt auch dann, wenn die Abklärungen noch laufen; wir ' +
            'ergänzen sie fortlaufend, sobald weitere Erkenntnisse vorliegen.'
        }
      ]
    },
    {
      titel: 'Nachweis und Kontrolle',
      absaetze: [
        {
          text:
            'Wir weisen die Einhaltung dieser Pflichten auf Verlangen nach. Du kannst sie ' +
            'selbst oder durch einen Dritten überprüfen lassen; der Zeitpunkt wird ' +
            'vorgängig abgestimmt, um den Betrieb für andere Kunden nicht zu ' +
            'beeinträchtigen.'
        }
      ]
    },
    {
      titel: 'Kontakt',
      absaetze: [
        { text: 'Fragen zu diesem Dokument beantworten wir schriftlich.' },
        { text: 'E-Mail: {{email}}' },
        { text: '{{betreiber}}', stark: true },
        { text: 'Sitz: {{sitz}}, {{land}}' }
      ]
    }
  ]
}

export const RECHTSTEXTE: Rechtstext[] = [AGB, DATENSCHUTZ, AVV, IMPRESSUM]

export function rechtstext(id: string): Rechtstext | undefined {
  return RECHTSTEXTE.find((r) => r.id === id)
}

/**
 * Text mit eingesetzten Angaben.
 *
 * Kommt eine Marke ohne hinterlegten Wert vor, wirft die Funktion. Das ist
 * Absicht: ein unbekannter Name im Text ist ein Fehler im Text, und er soll
 * beim Bauen auffallen und nicht auf der Seite.
 */
export function einsetzen(text: string): string {
  return text.replace(/\{\{(\w+)\}\}/g, (_, name: string) => {
    const wert = MARKEN[name]
    if (!wert) throw new Error(`Unbekannte Marke im Rechtstext: {{${name}}}`)
    return wert()
  })
}
